import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, CalendarIcon, TagIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, MinusSquareIcon, CreditCardIcon, FileSignatureIcon, CheckCircleIcon, ClockIcon, XCircleIcon, RefreshCwIcon } from 'lucide-react';
export function DocumentFilters({
  onFilterChange,
  locale = 'en-US'
}) {
  const [searchText, setSearchText] = useState('');
  const [documentTypes, setDocumentTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [vendor, setVendor] = useState('');
  const [entity, setEntity] = useState('');
  const [site, setSite] = useState('');
  const [tags, setTags] = useState([]);
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [hasAttachments, setHasAttachments] = useState(undefined);
  const [hasNotes, setHasNotes] = useState(undefined);
  const [hasWarnings, setHasWarnings] = useState(undefined);
  const [hasErrors, setHasErrors] = useState(undefined);
  const [isImported, setIsImported] = useState(undefined);
  // Sample filter options
  const documentTypeOptions = [{
    code: 'INV',
    label: 'Invoice',
    icon: ReceiptIcon
  }, {
    code: 'PO',
    label: 'Purchase Order',
    icon: ShoppingCartIcon
  }, {
    code: 'REQ',
    label: 'Requisition',
    icon: FileTextIcon
  }, {
    code: 'CN',
    label: 'Credit Note',
    icon: MinusSquareIcon
  }, {
    code: 'EXP',
    label: 'Expense Claim',
    icon: CreditCardIcon
  }, {
    code: 'CTR',
    label: 'Contract',
    icon: FileSignatureIcon
  }];
  const statusOptions = [{
    value: 'Draft',
    icon: ClockIcon
  }, {
    value: 'Pending Approval',
    icon: ClockIcon
  }, {
    value: 'Approved',
    icon: CheckCircleIcon
  }, {
    value: 'Rejected',
    icon: XCircleIcon
  }, {
    value: 'Exception',
    icon: XCircleIcon
  }, {
    value: 'Sent to Vendor',
    icon: ClockIcon
  }, {
    value: 'Completed',
    icon: CheckCircleIcon
  }, {
    value: 'Paid',
    icon: CheckCircleIcon
  }, {
    value: 'Active',
    icon: CheckCircleIcon
  }, {
    value: 'Processed',
    icon: CheckCircleIcon
  }];
  const tagOptions = ['IT Department', 'Finance', 'Marketing', 'Sales', 'Travel', 'Q4', 'Annual'];
  const vendorOptions = ['Office Depot', 'Dell Technologies', 'PrintWorks Inc.', 'Server Solutions Inc.'];
  const entityOptions = ['Global Technologies Inc.'];
  const siteOptions = ['New York HQ'];
  // Apply filters when any filter value changes
  useEffect(() => {
    const filters = {
      searchText,
      documentTypes,
      statuses,
      startDate,
      endDate,
      vendor,
      entity,
      site,
      tags,
      minAmount: minAmount ? parseFloat(minAmount) : undefined,
      maxAmount: maxAmount ? parseFloat(maxAmount) : undefined,
      hasAttachments,
      hasNotes,
      hasWarnings,
      hasErrors,
      isImported
    };
    onFilterChange(filters);
  }, [searchText, documentTypes, statuses, startDate, endDate, vendor, entity, site, tags, minAmount, maxAmount, hasAttachments, hasNotes, hasWarnings, hasErrors, isImported]);
  // Reset all filters
  const handleReset = () => {
    setSearchText('');
    setDocumentTypes([]);
    setStatuses([]);
    setStartDate('');
    setEndDate('');
    setVendor('');
    setEntity('');
    setSite('');
    setTags([]);
    setMinAmount('');
    setMaxAmount('');
    setHasAttachments(undefined);
    setHasNotes(undefined);
    setHasWarnings(undefined);
    setHasErrors(undefined);
    setIsImported(undefined);
  };
  // Toggle selection in multi-select filters
  const toggleSelection = (value, currentValues, setValues) => {
    if (currentValues.includes(value)) {
      setValues(currentValues.filter(v => v !== value));
    } else {
      setValues([...currentValues, value]);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-4812">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-4813">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-4814">
          Filter Documents
        </h3>
        <button onClick={handleReset} className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none" data-id="element-4815">
          <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-4816" />
          Reset Filters
        </button>
      </div>
      <div className="px-4 py-5 sm:px-6" data-id="element-4817">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4818">
          {/* Search Text */}
          <div className="col-span-1 md:col-span-3" data-id="element-4819">
            <div className="relative rounded-md shadow-sm" data-id="element-4820">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4821">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-4822" />
              </div>
              <input type="text" value={searchText} onChange={e => setSearchText(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search by ID, vendor, entity, or created by" data-id="element-4823" />
            </div>
          </div>
          {/* Document Types */}
          <div data-id="element-4824">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4825">
              Document Type
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-4826">
              {documentTypeOptions.map(type => <button key={type.code} onClick={() => toggleSelection(type.code, documentTypes, setDocumentTypes)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${documentTypes.includes(type.code) ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} data-id="element-4827">
                  <type.icon className="h-3 w-3 mr-1" data-id="element-4828" />
                  {type.label}
                </button>)}
            </div>
          </div>
          {/* Status */}
          <div data-id="element-4829">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4830">
              Status
            </label>
            <select value={statuses[0] || ''} onChange={e => {
            if (e.target.value === '') {
              setStatuses([]);
            } else {
              setStatuses([e.target.value]);
            }
          }} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-4831">
              <option value="" data-id="element-4832">All Statuses</option>
              {statusOptions.map(status => <option key={status.value} value={status.value} data-id="element-4833">
                  {status.value}
                </option>)}
            </select>
          </div>
          {/* Date Range */}
          <div data-id="element-4834">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4835">
              Date Range
            </label>
            <div className="flex space-x-2" data-id="element-4836">
              <div className="flex-1" data-id="element-4837">
                <div className="relative rounded-md shadow-sm" data-id="element-4838">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4839">
                    <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-4840" />
                  </div>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" data-id="element-4841" />
                </div>
              </div>
              <div className="flex-1" data-id="element-4842">
                <div className="relative rounded-md shadow-sm" data-id="element-4843">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4844">
                    <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-4845" />
                  </div>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" data-id="element-4846" />
                </div>
              </div>
            </div>
          </div>
          {/* Vendor */}
          <div data-id="element-4847">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4848">
              Vendor
            </label>
            <select value={vendor} onChange={e => setVendor(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-4849">
              <option value="" data-id="element-4850">All Vendors</option>
              {vendorOptions.map(v => <option key={v} value={v} data-id="element-4851">
                  {v}
                </option>)}
            </select>
          </div>
          {/* Entity */}
          <div data-id="element-4852">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4853">
              Entity
            </label>
            <select value={entity} onChange={e => setEntity(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-4854">
              <option value="" data-id="element-4855">All Entities</option>
              {entityOptions.map(e => <option key={e} value={e} data-id="element-4856">
                  {e}
                </option>)}
            </select>
          </div>
          {/* Site */}
          <div data-id="element-4857">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4858">
              Site
            </label>
            <select value={site} onChange={e => setSite(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-4859">
              <option value="" data-id="element-4860">All Sites</option>
              {siteOptions.map(s => <option key={s} value={s} data-id="element-4861">
                  {s}
                </option>)}
            </select>
          </div>
          {/* Amount Range */}
          <div data-id="element-4862">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4863">
              Amount Range
            </label>
            <div className="flex space-x-2" data-id="element-4864">
              <div className="flex-1" data-id="element-4865">
                <input type="number" value={minAmount} onChange={e => setMinAmount(e.target.value)} placeholder="Min" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-4866" />
              </div>
              <div className="flex-1" data-id="element-4867">
                <input type="number" value={maxAmount} onChange={e => setMaxAmount(e.target.value)} placeholder="Max" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-4868" />
              </div>
            </div>
          </div>
          {/* Tags */}
          <div data-id="element-4869">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4870">
              Tags
            </label>
            <select value={tags[0] || ''} onChange={e => {
            if (e.target.value === '') {
              setTags([]);
            } else {
              setTags([e.target.value]);
            }
          }} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-4871">
              <option value="" data-id="element-4872">All Tags</option>
              {tagOptions.map(tag => <option key={tag} value={tag} data-id="element-4873">
                  {tag}
                </option>)}
            </select>
          </div>
          {/* Additional Filters */}
          <div data-id="element-4874">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4875">
              Additional Filters
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-4876">
              <button onClick={() => setHasAttachments(hasAttachments === true ? undefined : true)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${hasAttachments === true ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} data-id="element-4877">
                Has Attachments
              </button>
              <button onClick={() => setHasNotes(hasNotes === true ? undefined : true)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${hasNotes === true ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} data-id="element-4878">
                Has Notes
              </button>
              <button onClick={() => setHasWarnings(hasWarnings === true ? undefined : true)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${hasWarnings === true ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} data-id="element-4879">
                Has Warnings
              </button>
              <button onClick={() => setHasErrors(hasErrors === true ? undefined : true)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${hasErrors === true ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} data-id="element-4880">
                Has Errors
              </button>
              <button onClick={() => setIsImported(isImported === true ? undefined : true)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${isImported === true ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} data-id="element-4881">
                Imported via OCR
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}